import { supabase } from '@/integrations/supabase/client';
import { SequenceTransformService } from '@/lib/search/SequenceTransformService';
import type { Sequence } from '@/types/sequence';

export interface LiveEvent { slug: string; name: string; blurb: string | null; endsAt: string }

export interface BundleItem { id: string; slug: string | null; title: string; priceCents: number; imageUrl: string | null }
export interface BundleView {
  id: string; slug: string; title: string; description: string | null; coverUrl: string | null;
  priceCents: number;
  seller: { id: string; name: string; username: string | null };
  items: BundleItem[];
}
export interface BundleSummary { id: string; slug: string; title: string; priceCents: number; coverUrl: string | null }

const cents = (dollars: number | null | undefined) => Math.round(Number(dollars ?? 0) * 100);

/** The Light Hunt event running now (Black Friday, Rewind), if any. */
export async function fetchLiveEvent(): Promise<LiveEvent | null> {
  const nowIso = new Date().toISOString();
  const { data } = await supabase
    .from('promotion_events')
    .select('slug, name, blurb, ends_at')
    .lte('starts_at', nowIso)
    .gt('ends_at', nowIso)
    .order('ends_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  return data ? { slug: data.slug, name: data.name, blurb: data.blurb, endsAt: data.ends_at } : null;
}

/** Sequences with a live sale, soonest-ending first, shaped for SequenceCard. */
async function fetchDealSequences(limit = 60): Promise<Sequence[]> {
  const { data: deals } = await supabase.rpc('list_deal_sequence_ids', { p_limit: limit });
  const ids = (deals ?? []).map((d) => d.sequence_id);
  if (ids.length === 0) return [];
  const { data: rows } = await supabase
    .from('sequences')
    .select('id, slug, title, name, price, is_free, image_url, album_cover_url, software_type, holiday_type, listing_kind, user_id, download_count, upvote_count, created_at, artists(name)')
    .in('id', ids)
    .is('unlisted_at', null);
  const byId = new Map((rows ?? []).map((r) => [r.id, r]));
  return ids.flatMap((id) => {
    const r = byId.get(id) as (Record<string, unknown> & { artists?: { name?: string } | null }) | undefined;
    if (!r) return [];
    const s = SequenceTransformService.transformHitToSequence({ ...r, holiday: r.holiday_type, artist_name: r.artists?.name });
    return s ? [s] : [];
  });
}

export async function fetchDeals(): Promise<{ event: LiveEvent | null; sequences: Sequence[] }> {
  const [event, sequences] = await Promise.all([fetchLiveEvent(), fetchDealSequences()]);
  return { event, sequences };
}

/** True while any sale or event is live. One cheap call per minute, shared by every caller. */
let liveCache: { at: number; promise: Promise<boolean> } | null = null;
export function fetchHasLiveDeals(): Promise<boolean> {
  if (liveCache && Date.now() - liveCache.at < 60_000) return liveCache.promise;
  const promise = (async () => {
    const [{ data }, event] = await Promise.all([supabase.rpc('list_deal_sequence_ids', { p_limit: 1 }), fetchLiveEvent()]);
    return Boolean(event) || (data ?? []).length > 0;
  })().catch(() => { liveCache = null; return false; });
  liveCache = { at: Date.now(), promise };
  return promise;
}

export async function fetchBundleBySlug(slug: string): Promise<BundleView | null> {
  const { data: b, error } = await supabase
    .from('sequence_bundles')
    .select('id, slug, title, description, cover_url, price, seller_id')
    .eq('slug', slug)
    .not('listed_at', 'is', null)
    .maybeSingle();
  if (error) throw error;
  if (!b) return null;
  const [{ data: links, error: linkError }, { data: profile }] = await Promise.all([
    supabase.from('sequence_bundle_items').select('sequence_id').eq('bundle_id', b.id),
    supabase.from('user_profiles').select('display_name, username').eq('user_id', b.seller_id).maybeSingle(),
  ]);
  if (linkError) throw linkError;
  const ids = (links ?? []).map((l) => l.sequence_id);
  const { data: seqs, error: seqError } = ids.length
    ? await supabase.from('sequences').select('id, slug, title, price, image_url').in('id', ids)
    : { data: [], error: null };
  if (seqError) throw seqError;
  return {
    id: b.id, slug: b.slug, title: b.title, description: b.description, coverUrl: b.cover_url,
    priceCents: cents(b.price),
    seller: { id: b.seller_id, name: profile?.display_name || profile?.username || 'This creator', username: profile?.username ?? null },
    items: (seqs ?? []).map((s) => ({ id: s.id, slug: s.slug, title: s.title ?? 'Untitled', priceCents: cents(s.price), imageUrl: s.image_url })),
  };
}

export async function fetchSellerBundles(sellerId: string): Promise<BundleSummary[]> {
  const { data } = await supabase
    .from('sequence_bundles')
    .select('id, slug, title, price, cover_url')
    .eq('seller_id', sellerId)
    .not('listed_at', 'is', null)
    .order('listed_at', { ascending: false });
  return (data ?? []).map((b) => ({ id: b.id, slug: b.slug, title: b.title, priceCents: cents(b.price), coverUrl: b.cover_url }));
}
